import { useEffect } from 'react';
import { Button } from '@/shared/components/ui/button';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import { RefreshCw, AlertCircle, Headphones } from 'lucide-react';
import { cn } from '@/shared/utils/cn';
import type { Message, ConnectionState } from '../hooks/useStreamingChat';

interface ChatMessageListProps {
  messages: Message[];
  isTyping: boolean;
  connectionState: ConnectionState;
  scrollRef: React.RefObject<HTMLDivElement>;
  onRetry: () => void;
  onLiveOperator: () => void;
  isLoading: boolean;
}

export function ChatMessageList({
  messages,
  isTyping,
  connectionState,
  scrollRef,
  onRetry,
  onLiveOperator,
  isLoading,
}: ChatMessageListProps) {
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping, scrollRef]);

  return (
    <ScrollArea className="flex-1 px-4 py-3" ref={scrollRef}>
      <div className="space-y-3">
        {messages.map((message, index) => (
          <div
            key={index}
            className={cn(
              'flex gap-2.5 items-end',
              message.role === 'user' ? 'justify-end' : 'justify-start'
            )}
          >
            {message.role === 'assistant' && (
              <Avatar className="h-7 w-7 border shadow-xs mb-0.5 shrink-0">
                <AvatarFallback className={cn(
                  'font-bold text-[10px]',
                  message.isError
                    ? 'bg-destructive text-destructive-foreground'
                    : 'bg-primary/10 text-primary'
                )}>
                  {message.isError ? <AlertCircle className="h-3.5 w-3.5" /> : 'DP'}
                </AvatarFallback>
              </Avatar>
            )}
            <div className="flex flex-col gap-1 max-w-[85%]">
              <div
                className={cn(
                  'rounded-2xl px-3.5 py-2.5 text-sm',
                  message.role === 'user'
                    ? 'bg-primary text-primary-foreground rounded-br-md'
                    : message.isError
                      ? 'bg-destructive/10 border border-destructive/20 text-destructive rounded-bl-md'
                      : 'bg-muted/70 rounded-bl-md'
                )}
              >
                <p className="whitespace-pre-wrap leading-relaxed">{message.content}</p>
              </div>
              {message.isError && index === messages.length - 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onRetry}
                  className="self-start h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 gap-1.5 px-2"
                >
                  <RefreshCw className="h-3 w-3" />
                  Try again
                </Button>
              )}
            </div>
          </div>
        ))}

        {isTyping && !messages.some(m => m.role === 'assistant' && !m.isError && messages.indexOf(m) === messages.length - 1) && (
          <div className="flex gap-2.5 items-end justify-start">
            <Avatar className="h-7 w-7 border shadow-xs mb-0.5 shrink-0">
              <AvatarFallback className="bg-primary/10 text-primary font-bold text-[10px]">
                DP
              </AvatarFallback>
            </Avatar>
            <div className="rounded-2xl rounded-bl-md px-4 py-3 bg-muted/70">
              <div className="flex gap-1">
                <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        {connectionState === 'connecting' && (
          <div className="flex items-center justify-center gap-2 py-2 text-xs text-muted-foreground">
            <div className="h-3 w-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            Connecting to Dr. Phillip...
          </div>
        )}

        {messages.length > 2 && !messages[messages.length - 1]?.isError && !isLoading && (
          <div className="pt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={onLiveOperator}
              className="w-full justify-center gap-2 h-8 text-xs bg-emerald-50/50 hover:bg-emerald-50 text-emerald-700 border-emerald-200/50 hover:border-emerald-200"
            >
              <Headphones className="h-3.5 w-3.5" />
              Need more help? Talk to a live operator
            </Button>
          </div>
        )}
      </div>
    </ScrollArea>
  );
}
